/**
 * LeagueGameSheetPage — /league/:leagueId/game/:gameId
 * ─────────────────────────────────────────────────────────────────
 * Phase 5a. The rinkside-friendly per-game data entry surface.
 *
 * THREE TABS
 *   Lineup    — pre-game roster confirmation per team. Coaches
 *               edit their own side; officials/managers see both.
 *   Scoring   — live goal entry + running scoreboard.
 *   Penalties — live penalty entry.
 *
 * AUTH MIRROR
 *   The backend tells us (via `editable_team_ids`) which teams the
 *   current user can write to. We render edit controls only for
 *   those teams. Everything else is read-only.
 *
 * MOBILE-FIRST DESIGN
 *   Sticky scoreboard at the top. Tab strip is large-tap-target.
 *   Modals are full-width on narrow viewports. Player picker
 *   ALWAYS shows a search bar — quicker than scrolling on a phone.
 *
 * Sign-off / scoresheet-lock UI lands in Phase 5b along with the
 * signature-pad component.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ChevronLeft, Calendar, MapPin, Shield, Target, AlertOctagon,
  Plus, X, Trash2, Edit2, Check, Search, Users,
  Lock, Unlock, PenLine, Award, AlertTriangle,
  // Phase G — printable scoresheet.
  Printer,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import SignaturePad from '../../components/SignaturePad.jsx';
// (2026-06-09, second pass) The per-game bench-to-bench chat tab was
// removed on Gilles's direction — chat is within-team (team chat) or
// coach-to-coach (league coaches channel) only.
// Phase G — print-ready scoresheet (browser print → paper or PDF).
import { printScoresheet } from '../../utils/printScoresheet.js';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
// v2.5.125 — Phase 11 date consistency.
import { formatDate } from '../../utils/formatDate.js';

const PERIOD_LABELS = { 1: '1st', 2: '2nd', 3: '3rd', 4: 'OT', 5: 'SO' };

// Penalty quick-picks — covers the common cases. Coaches/scorekeepers
// can type any infraction; this list just speeds up entry on a phone.
const COMMON_INFRACTIONS = [
  'Tripping', 'Hooking', 'Slashing', 'Roughing', 'Holding',
  'Interference', 'Cross-checking', 'High-sticking', 'Boarding',
  'Charging', 'Delay of game', 'Too many men', 'Unsportsmanlike',
  'Fighting', 'Checking from behind', 'Spearing', 'Misconduct',
];

const COACH_POSITION_OPTIONS = [
  { v: 'head_coach',      l: 'Head coach' },
  { v: 'assistant_coach', l: 'Assistant coach' },
  { v: 'trainer',         l: 'Trainer' },
  { v: 'manager',         l: 'Manager' },
  { v: 'other',           l: 'Other' },
];

export default function LeagueGameSheetPage() {
  const { leagueId, gameId } = useParams();

  const [sheetsData, setSheetsData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [penalties, setPenalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('lineup');

  const reload = useCallback(async () => {
    try {
      const [sRes, gRes, pRes] = await Promise.all([
        api.get(`/leagues/${leagueId}/games/${gameId}/sheets`),
        api.get(`/leagues/${leagueId}/games/${gameId}/goals`).catch(() => ({ data: { goals: [] } })),
        api.get(`/leagues/${leagueId}/games/${gameId}/penalties`).catch(() => ({ data: { penalties: [] } })),
      ]);
      setSheetsData(sRes.data || null);
      setGoals(gRes.data?.goals || []);
      setPenalties(pRes.data?.penalties || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Not authorized to view this game sheet');
      } else if (err?.response?.status === 404) {
        toast.error('Game not found');
      } else if (err?.response?.status !== 401) {
        toast.error('Could not load game');
      }
    } finally {
      setLoading(false);
    }
  }, [leagueId, gameId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 960 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!sheetsData?.game) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 960 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          Game not found or not accessible.
        </div>
      </div>
    );
  }

  const { game, sheets, lineup, coaches, editable_team_ids } = sheetsData;
  const canEditAnything = (editable_team_ids || []).length > 0;
  const locked = game.is_scoresheet_locked;

  const homeSheet = sheets.find(s => s.team_id === game.home_team_id) || null;
  const awaySheet = sheets.find(s => s.team_id === game.away_team_id) || null;

  // Scoreboard from live goal data (not from games.result_*_score
  // which is set at finalize time — Phase 5b). This way the
  // scoreboard updates in real time as goals are added.
  const homeScore = goals.filter(g => g.team_id === game.home_team_id).length;
  const awayScore = goals.filter(g => g.team_id === game.away_team_id).length;

  // Phase G — assemble everything already on this page (plus a quick
  // officials fetch) into the print-ready scoresheet window. The
  // browser's print dialog covers both paper and save-as-PDF.
  async function handlePrint() {
    let officials = [];
    try {
      const { data } = await api.get(`/leagues/${leagueId}/games/${gameId}/officials`);
      officials = data?.officials || [];
    } catch { /* officials are optional on the printout */ }
    const ok = printScoresheet({
      leagueName: null,
      game, homeSheet, awaySheet,
      lineup: {
        home: homeSheet ? (lineup[homeSheet.id] || []) : [],
        away: awaySheet ? (lineup[awaySheet.id] || []) : [],
      },
      coaches: {
        home: homeSheet ? (coaches[homeSheet.id] || []) : [],
        away: awaySheet ? (coaches[awaySheet.id] || []) : [],
      },
      goals, penalties, officials, homeScore, awayScore,
    });
    if (!ok) toast.error('Pop-up blocked — allow pop-ups for this site to print');
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Link to={`/league/${leagueId}/schedule`} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 12, color: 'var(--text-2)', textDecoration: 'none',
        }}>
          <ChevronLeft size={14} />
          Back to schedule
        </Link>
        {/* Phase G — archival/printable scoresheet. */}
        <button type="button" className="btn-ghost" onClick={handlePrint}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <Printer size={14} /> Print scoresheet
        </button>
      </div>

      {/* Scoreboard header */}
      <ScoreboardCard game={game} homeScore={homeScore} awayScore={awayScore} />

      {locked && (
        <div className="card" style={{
          background: 'var(--puck-a15)', borderColor: 'var(--puck-a40)',
          padding: 12, marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Lock size={16} color="var(--puck)" />
          <div style={{ fontSize: 13 }}>
            <strong>Scoresheet locked.</strong> This game is final — view only.
          </div>
        </div>
      )}

      {!canEditAnything && !locked && (
        <div className="card" style={{
          background: 'var(--surface-2)', padding: 12, marginBottom: 14,
          fontSize: 12, color: 'var(--text-2)',
        }}>
          You're viewing this game sheet as a league member. Only coaches of either team,
          assigned officials, and managers can edit it.
        </div>
      )}

      {/* Tab strip */}
      <div style={{
        display: 'flex', gap: 4, padding: 4,
        background: 'var(--surface-2)', borderRadius: 12,
        border: '1px solid var(--border)', marginBottom: 16, flexWrap: 'wrap',
      }}>
        <TabBtn active={tab === 'lineup'}    onClick={() => setTab('lineup')}    icon={Users}>Lineup</TabBtn>
        <TabBtn active={tab === 'scoring'}   onClick={() => setTab('scoring')}   icon={Target}>Scoring</TabBtn>
        <TabBtn active={tab === 'penalties'} onClick={() => setTab('penalties')} icon={AlertOctagon}>Penalties</TabBtn>
        <TabBtn active={tab === 'signoff'}   onClick={() => setTab('signoff')}   icon={Award}>Sign-off</TabBtn>
      </div>

      {tab === 'lineup' && (
        <LineupTab
          suspendedProfileIds={sheetsData.suspended_profile_ids || []}
          leagueId={leagueId} gameId={gameId}
          game={game}
          sheets={sheets}
          homeSheet={homeSheet} awaySheet={awaySheet}
          lineup={lineup} coaches={coaches}
          editableTeamIds={editable_team_ids || []}
          locked={locked}
          onChanged={reload}
        />
      )}
      {tab === 'scoring' && (
        <ScoringTab
          leagueId={leagueId} gameId={gameId}
          game={game}
          goals={goals}
          lineup={lineup}
          homeSheet={homeSheet} awaySheet={awaySheet}
          editableTeamIds={editable_team_ids || []}
          locked={locked}
          onChanged={reload}
        />
      )}
      {tab === 'penalties' && (
        <PenaltiesTab
          leagueId={leagueId} gameId={gameId}
          game={game}
          penalties={penalties}
          lineup={lineup}
          homeSheet={homeSheet} awaySheet={awaySheet}
          editableTeamIds={editable_team_ids || []}
          locked={locked}
          onChanged={reload}
        />
      )}
      {tab === 'signoff' && (
        <SignoffTab
          leagueId={leagueId} gameId={gameId}
          game={game}
          homeSheet={homeSheet} awaySheet={awaySheet}
          editableTeamIds={editable_team_ids || []}
          locked={locked}
          onChanged={reload}
        />
      )}
    </div>
  );
}

// ─── Scoreboard header ────────────────────────────────────────

function ScoreboardCard({ game, homeScore, awayScore }) {
  return (
    <div className="card" style={{
      padding: 16, marginBottom: 14,
      background: 'linear-gradient(180deg, var(--surface-2), var(--surface))',
    }}>
      {/* Game meta line */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        fontSize: 11, color: 'var(--text-2)',
        marginBottom: 14, flexWrap: 'wrap',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Calendar size={11} /> {formatDate(game.game_date)} {(game.game_time || '').slice(0, 5)}
        </span>
        {game.venue_name && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <MapPin size={11} /> {game.venue_name}
          </span>
        )}
      </div>
      {/* Score row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <TeamScore name={game.home_team_name || 'Home'} score={homeScore} align="left" />
        <div style={{
          fontSize: 11, color: 'var(--text-3)', fontWeight: 700,
          padding: '0 6px', textTransform: 'uppercase', letterSpacing: 0.8,
        }}>
          vs
        </div>
        <TeamScore name={game.away_team_name || 'Away'} score={awayScore} align="right" />
      </div>
    </div>
  );
}

function TeamScore({ name, score, align }) {
  return (
    <div style={{
      flex: 1, minWidth: 0,
      display: 'flex', flexDirection: align === 'left' ? 'row' : 'row-reverse',
      alignItems: 'center', gap: 12,
    }}>
      <div style={{
        fontSize: 36, fontWeight: 800, lineHeight: 1,
        fontFamily: 'var(--font-mono)',
        color: 'var(--text)',
        minWidth: 40, textAlign: align === 'left' ? 'left' : 'right',
      }}>
        {score}
      </div>
      <div style={{
        flex: 1, minWidth: 0,
        textAlign: align,
        fontSize: 14, fontWeight: 700,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {name}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '10px 16px', borderRadius: 8,
      fontSize: 13, fontWeight: 600,
      color: active ? 'var(--text)' : 'var(--text-2)',
      background: active ? 'var(--surface)' : 'transparent',
      border: 'none',
      boxShadow: active ? 'var(--shadow-sm)' : 'none',
      cursor: 'pointer',
      flex: '1 1 100px',
      justifyContent: 'center',
    }}>
      <Icon size={14} />
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// LINEUP TAB
// ═══════════════════════════════════════════════════════════════

function LineupTab({
  leagueId, gameId, game, sheets, homeSheet, awaySheet,
  lineup, coaches, editableTeamIds, locked, onChanged,
  // Phase H — active-suspension warning badges.
  suspendedProfileIds = [],
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SheetSection
        side="Home" teamName={game.home_team_name || 'Home'}
        teamId={game.home_team_id}
        sheet={homeSheet}
        lineup={homeSheet ? (lineup[homeSheet.id] || []) : []}
        coaches={homeSheet ? (coaches[homeSheet.id] || []) : []}
        canEdit={!locked && editableTeamIds.includes(game.home_team_id)}
        leagueId={leagueId} gameId={gameId} seasonId={game.league_season_id}
        onChanged={onChanged}
        suspendedProfileIds={suspendedProfileIds}
      />
      <SheetSection
        side="Away" teamName={game.away_team_name || 'Away'}
        teamId={game.away_team_id}
        sheet={awaySheet}
        lineup={awaySheet ? (lineup[awaySheet.id] || []) : []}
        coaches={awaySheet ? (coaches[awaySheet.id] || []) : []}
        canEdit={!locked && editableTeamIds.includes(game.away_team_id)}
        leagueId={leagueId} gameId={gameId} seasonId={game.league_season_id}
        onChanged={onChanged}
        suspendedProfileIds={suspendedProfileIds}
      />
    </div>
  );
}

function SheetSection({ side, teamName, teamId, sheet, lineup, coaches, canEdit, leagueId, gameId, seasonId, onChanged, suspendedProfileIds = [] }) {
  const [creating, setCreating]   = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showAddCoach, setShowAddCoach]   = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  async function createSheet() {
    setCreating(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/games/${gameId}/sheets`, { team_id: teamId });
      toast.success(data.created
        ? `Sheet created · seeded ${data.seeded_count || 0} player${data.seeded_count === 1 ? '' : 's'} from roster`
        : 'Sheet already exists');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not create sheet');
    } finally {
      setCreating(false);
    }
  }

  if (!sheet) {
    return (
      <div className="card" style={{ padding: 18 }}>
        <SectionHeader side={side} teamName={teamName} />
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
          No sheet started for this team yet.
        </div>
        {canEdit ? (
          <button type="button" className="btn-ice" onClick={createSheet} disabled={creating}>
            <PenLine size={13} style={{ marginRight: 6 }} />
            {creating ? 'Creating…' : 'Start sheet (seed from roster)'}
          </button>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
            Only this team's coach (or an assigned official) can start the sheet.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
        <SectionHeader side={side} teamName={teamName} finalized={sheet.is_finalized} />
        {sheet.is_finalized && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
            fontSize: 11, color: 'var(--text-2)',
          }}>
            <Check size={12} color="var(--goal)" />
            <span>Signed by <strong style={{ color: 'var(--text)' }}>{sheet.signed_by_name}</strong>{' '}
            on {formatDateTime(sheet.signed_at)}</span>
          </div>
        )}
      </div>

      {/* Lineup */}
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Lineup ({lineup.length})
          </div>
          {canEdit && !sheet.is_finalized && (
            <button type="button" className="btn-ghost" style={tinyBtn} onClick={() => setShowAddPlayer(true)}>
              <Plus size={11} style={{ marginRight: 4 }} />
              Add player
            </button>
          )}
        </div>
        {lineup.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic', padding: '6px 0' }}>
            No players. {canEdit && !sheet.is_finalized && 'Add players from the season roster or as ad-hoc.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {lineup.map(row => (
              <LineupRow key={row.id} row={row}
                canEdit={canEdit && !sheet.is_finalized}
                suspended={!!row.player_profile_id && suspendedProfileIds.includes(row.player_profile_id)}
                onEdit={() => setEditingPlayer(row)} />
            ))}
          </div>
        )}
      </div>

      {/* Coaches */}
      <div style={{ padding: 16, borderTop: '1px solid var(--border)', background: 'var(--surface-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Bench staff ({coaches.length})
          </div>
          {canEdit && !sheet.is_finalized && (
            <button type="button" className="btn-ghost" style={tinyBtn} onClick={() => setShowAddCoach(true)}>
              <Plus size={11} style={{ marginRight: 4 }} />
              Add coach
            </button>
          )}
        </div>
        {coaches.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>
            No bench staff added.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {coaches.map(c => (
              <CoachRow key={c.id} coach={c}
                canEdit={canEdit && !sheet.is_finalized}
                leagueId={leagueId} gameId={gameId} sheetId={sheet.id}
                onChanged={onChanged} />
            ))}
          </div>
        )}
      </div>

      {/* Finalize footer */}
      {canEdit && (
        <div style={{
          padding: 14, borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap',
        }}>
          {sheet.is_finalized ? (
            <button type="button" className="btn-ghost" disabled={finalizing}
              onClick={async () => {
                if (!window.confirm('Reopen this sheet for editing?')) return;
                setFinalizing(true);
                try {
                  await api.post(`/leagues/${leagueId}/games/${gameId}/sheets/${sheet.id}/reopen`);
                  toast.success('Sheet reopened');
                  onChanged();
                } catch (err) {
                  toast.error(err?.response?.data?.error || 'Could not reopen');
                } finally { setFinalizing(false); }
              }}>
              <Edit2 size={12} style={{ marginRight: 6 }} />
              {finalizing ? 'Reopening…' : 'Reopen sheet'}
            </button>
          ) : (
            <button type="button" className="btn-ice"
              onClick={() => setFinalizing('open')}
              disabled={lineup.length === 0}>
              <Check size={12} style={{ marginRight: 6 }} />
              Finalize lineup
            </button>
          )}
        </div>
      )}

      {showAddPlayer && (
        <AddPlayerModal
          leagueId={leagueId} gameId={gameId} seasonId={seasonId}
          sheetId={sheet.id} teamId={teamId} teamName={teamName}
          existingJerseys={lineup.map(l => l.jersey_number)}
          onClose={() => setShowAddPlayer(false)}
          onAdded={() => { setShowAddPlayer(false); onChanged(); }}
        />
      )}
      {editingPlayer && (
        <EditPlayerModal
          leagueId={leagueId} gameId={gameId}
          sheetId={sheet.id} row={editingPlayer}
          existingJerseys={lineup.filter(l => l.id !== editingPlayer.id).map(l => l.jersey_number)}
          onClose={() => setEditingPlayer(null)}
          onSaved={() => { setEditingPlayer(null); onChanged(); }}
        />
      )}
      {showAddCoach && (
        <AddCoachModal
          leagueId={leagueId} gameId={gameId} sheetId={sheet.id}
          onClose={() => setShowAddCoach(false)}
          onAdded={() => { setShowAddCoach(false); onChanged(); }}
        />
      )}
      {finalizing === 'open' && (
        <FinalizeModal
          leagueId={leagueId} gameId={gameId} sheetId={sheet.id} teamName={teamName}
          onClose={() => setFinalizing(false)}
          onDone={() => { setFinalizing(false); onChanged(); }}
        />
      )}
    </div>
  );
}

function SectionHeader({ side, teamName, finalized }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{
        padding: '2px 8px', borderRadius: 6,
        background: side === 'Home' ? 'var(--ice-a10)' : 'var(--puck-a15)',
        color:      side === 'Home' ? 'var(--ice)'    : 'var(--puck)',
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7,
      }}>{side}</span>
      <span style={{ fontSize: 16, fontWeight: 700 }}>{teamName}</span>
      {finalized && (
        <span className="badge" style={{
          background: 'var(--goal-a20)', color: 'var(--goal)',
          fontSize: 10, padding: '2px 8px',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <Check size={10} />
          Finalized
        </span>
      )}
    </div>
  );
}

function LineupRow({ row, canEdit, onEdit, suspended }) {
  const isAdHoc = !row.player_profile_id;
  return (
    <button type="button"
      onClick={canEdit ? onEdit : undefined}
      disabled={!canEdit}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 10px', borderRadius: 8,
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        color: 'var(--text)', cursor: canEdit ? 'pointer' : 'default',
        textAlign: 'left', width: '100%',
      }}>
      <div style={{
        flexShrink: 0, width: 36, height: 36, borderRadius: 8,
        background: 'var(--ice-a10)', color: 'var(--ice)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-mono)',
      }}>
        {row.jersey_number}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {row.display_name || row.ad_hoc_name || '(unnamed)'}
          {row.is_captain && <Badge label="C" color="var(--ice)" />}
          {row.is_alternate_captain && <Badge label="A" color="var(--ice)" />}
          {row.is_starting && <Badge label="START" color="var(--goal)" />}
          {/* Phase H — active league suspension warning (app-level;
              the league decides whether to pull the player). */}
          {suspended && <Badge label="⚠ SUSPENDED" color="var(--penalty, #ff4444)" />}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
          {row.position || '—'}
          {isAdHoc && <> · <span style={{ color: 'var(--puck)' }}>ad-hoc</span></>}
          {row.is_affiliate && <> · <span style={{ color: 'var(--text-2)' }}>affiliate</span></>}
        </div>
      </div>
      {canEdit && <Edit2 size={12} color="var(--text-2)" />}
    </button>
  );
}

function Badge({ label, color }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
      padding: '1px 5px', borderRadius: 4,
      border: `1px solid ${color}`, color, background: 'transparent',
    }}>{label}</span>
  );
}

function CoachRow({ coach, canEdit, leagueId, gameId, sheetId, onChanged }) {
  async function remove() {
    if (!window.confirm(`Remove ${coach.coach_name}?`)) return;
    try {
      await api.delete(`/leagues/${leagueId}/games/${gameId}/sheets/${sheetId}/coaches/${coach.id}`);
      toast.success('Removed');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not remove');
    }
  }
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '6px 10px', borderRadius: 8,
      background: 'var(--surface)', border: '1px solid var(--border)',
    }}>
      <Shield size={12} color="var(--text-2)" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600 }}>{coach.coach_name}</div>
        <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
          {COACH_POSITION_OPTIONS.find(p => p.v === coach.coach_position)?.l || coach.coach_position}
        </div>
      </div>
      {canEdit && (
        <button type="button" className="btn-ghost"
          style={{ ...tinyBtn, color: 'var(--penalty)' }} onClick={remove}>
          <Trash2 size={10} />
        </button>
      )}
    </div>
  );
}

// ─── Add/Edit player modals ───────────────────────────────────

function AddPlayerModal({ leagueId, gameId, sheetId, teamId, teamName, seasonId, existingJerseys, onClose, onAdded }) {
  const [mode, setMode]   = useState('roster');  // 'roster' | 'adhoc'
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [adHocName, setAdHocName] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [position, setPosition] = useState('');
  const [isStarting, setIsStarting]       = useState(false);
  const [isCaptain, setIsCaptain]         = useState(false);
  const [isAlt, setIsAlt]                 = useState(false);
  const [isAffiliate, setIsAffiliate]     = useState(false);
  const [saving, setSaving] = useState(false);

  // Load the team's league roster via the Phase 3 by-team endpoint.
  // That endpoint returns snake_case fields including player_profile_id,
  // which is exactly what we need to add lineup rows. Note: it returns
  // ONLY league-allocated players (team_members with non-null
  // player_profile_id); non-league members are excluded by design.
  useEffect(() => {
    if (!seasonId) { setRosterLoading(false); return; }
    let cancelled = false;
    api.get(`/leagues/${leagueId}/seasons/${seasonId}/players/by-team`)
      .then(({ data }) => {
        if (cancelled) return;
        const team = (data?.teams || []).find(t => t.team_id === teamId);
        setRoster(team?.roster || []);
      })
      .catch(() => { if (!cancelled) setRoster([]); })
      .finally(() => { if (!cancelled) setRosterLoading(false); });
    return () => { cancelled = true; };
  }, [leagueId, teamId, seasonId]);

  const filteredRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter(m => (m.player_name || '').toLowerCase().includes(q));
  }, [roster, search]);

  function pickRosterMember(m) {
    setSelectedProfileId(m.player_profile_id);
    if (m.jersey_number != null) setJerseyNumber(String(m.jersey_number));
    if (m.position) setPosition(m.position);
  }

  async function submit() {
    const body = {
      jersey_number: parseInt(jerseyNumber, 10),
      position: position || null,
      is_starting: isStarting,
      is_captain: isCaptain,
      is_alternate_captain: isAlt,
      is_affiliate: isAffiliate,
    };
    if (mode === 'roster') {
      if (!selectedProfileId) return toast.error('Pick a player from the roster');
      body.player_profile_id = selectedProfileId;
    } else {
      if (!adHocName.trim()) return toast.error('Ad-hoc name required');
      body.ad_hoc_name = adHocName.trim();
    }
    if (!Number.isInteger(body.jersey_number) || body.jersey_number < 0 || body.jersey_number > 999) {
      return toast.error('Jersey number must be 0-999');
    }
    if (existingJerseys.includes(body.jersey_number)) {
      return toast.error(`#${body.jersey_number} is already taken in this lineup`);
    }
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/games/${gameId}/sheets/${sheetId}/lineup`, body);
      toast.success('Added');
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not add player');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile pass — plain vh doesn't shrink when the on-screen keyboard
          opens (this modal has a search field + jersey/position inputs),
          so the Add button could end up hidden under the keyboard on a
          phone. var(--mobile-h) tracks the real visible area (falls back
          to 100dvh on desktop), same fix already applied to SparesTab /
          GamePage / SchedulePage modals. */}
      <div className="card" style={{ maxWidth: 560, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title={`Add to ${teamName}`} onClose={onClose} />

        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          <ModeBtn active={mode === 'roster'} onClick={() => setMode('roster')}>From roster</ModeBtn>
          <ModeBtn active={mode === 'adhoc'}  onClick={() => setMode('adhoc')}>Ad-hoc / guest</ModeBtn>
        </div>

        {mode === 'roster' && (
          <>
            <Field label="Search roster">
              <div style={{ position: 'relative' }}>
                <Search size={12} color="var(--text-2)" style={{
                  position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                }} />
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="By name…" style={{ ...inputStyle, paddingLeft: 28 }} />
              </div>
            </Field>
            <div style={{
              maxHeight: 200, overflowY: 'auto',
              border: '1px solid var(--border)', borderRadius: 8,
              marginBottom: 14,
            }}>
              {rosterLoading ? (
                <div style={{ padding: 12, fontSize: 12, color: 'var(--text-2)', textAlign: 'center' }}>Loading roster…</div>
              ) : filteredRoster.length === 0 ? (
                <div style={{ padding: 12, fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>
                  {roster.length === 0
                    ? 'No league-allocated players on this team yet. Use the ad-hoc tab for guests.'
                    : 'No roster matches.'}
                </div>
              ) : (
                filteredRoster.map(m => {
                  const sel = selectedProfileId && m.player_profile_id === selectedProfileId;
                  return (
                    <button key={m.team_member_id} type="button" onClick={() => pickRosterMember(m)}
                      style={{
                        width: '100%', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', borderBottom: '1px solid var(--border)',
                        background: sel ? 'var(--ice-a10)' : 'transparent',
                        border: 'none', cursor: 'pointer',
                        color: 'var(--text)',
                      }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'var(--surface-2)', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: 'var(--text-2)',
                      }}>
                        {(m.player_name || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{m.player_name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
                          #{m.jersey_number ?? '—'} · {m.position || '—'}
                        </div>
                      </div>
                      {sel && <Check size={14} color="var(--ice)" />}
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}

        {mode === 'adhoc' && (
          <Field label="Player name" hint="A guest sub or affiliate with no Game Day profile yet.">
            <input type="text" value={adHocName} onChange={(e) => setAdHocName(e.target.value)}
              placeholder="e.g. John Doe" maxLength={120} autoFocus
              style={inputStyle} />
          </Field>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Field label="Jersey #" style={{ flex: '1 1 100px' }}>
            <input type="number" value={jerseyNumber} min={0} max={999}
              onChange={(e) => setJerseyNumber(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Position" style={{ flex: '1 1 140px' }}>
            <select value={position} onChange={(e) => setPosition(e.target.value)} style={inputStyle}>
              <option value="">—</option>
              <option value="forward">Forward</option>
              <option value="defense">Defense</option>
              <option value="goalie">Goalie</option>
              <option value="flex">Flex</option>
            </select>
          </Field>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '14px 0' }}>
          <Toggle label="Starter"  checked={isStarting} onChange={setIsStarting} />
          <Toggle label="Captain"  checked={isCaptain}  onChange={(v) => { setIsCaptain(v); if (v) setIsAlt(false); }} />
          <Toggle label="Alt-Cpt"  checked={isAlt}      onChange={(v) => { setIsAlt(v); if (v) setIsCaptain(false); }} />
          <Toggle label="Affiliate" checked={isAffiliate} onChange={setIsAffiliate} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving}>
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function EditPlayerModal({ leagueId, gameId, sheetId, row, existingJerseys, onClose, onSaved }) {
  const [jersey, setJersey] = useState(String(row.jersey_number));
  const [position, setPosition] = useState(row.position || '');
  const [isStarting, setIsStarting] = useState(row.is_starting);
  const [isCaptain, setIsCaptain]   = useState(row.is_captain);
  const [isAlt, setIsAlt]           = useState(row.is_alternate_captain);
  const [isAffiliate, setIsAffiliate] = useState(row.is_affiliate);
  const [saving, setSaving] = useState(false);

  async function save() {
    const newJersey = parseInt(jersey, 10);
    if (!Number.isInteger(newJersey) || newJersey < 0 || newJersey > 999) {
      return toast.error('Jersey number must be 0-999');
    }
    if (newJersey !== row.jersey_number && existingJerseys.includes(newJersey)) {
      return toast.error(`#${newJersey} is already taken`);
    }
    setSaving(true);
    try {
      await api.patch(`/leagues/${leagueId}/games/${gameId}/sheets/${sheetId}/lineup/${row.id}`, {
        jersey_number: newJersey,
        position: position || null,
        is_starting: isStarting,
        is_captain: isCaptain,
        is_alternate_captain: isAlt,
        is_affiliate: isAffiliate,
      });
      toast.success('Saved');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  }
  async function remove() {
    if (!window.confirm(`Remove ${row.display_name || row.ad_hoc_name} from the lineup?`)) return;
    try {
      await api.delete(`/leagues/${leagueId}/games/${gameId}/sheets/${sheetId}/lineup/${row.id}`);
      toast.success('Removed');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not remove');
    }
  }
  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile pass — same vh/keyboard fix as AddPlayerModal above. */}
      <div className="card" style={{ maxWidth: 480, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title={`Edit ${row.display_name || row.ad_hoc_name}`} onClose={onClose} />

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Field label="Jersey #" style={{ flex: '1 1 100px' }}>
            <input type="number" value={jersey} min={0} max={999}
              onChange={(e) => setJersey(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Position" style={{ flex: '1 1 140px' }}>
            <select value={position} onChange={(e) => setPosition(e.target.value)} style={inputStyle}>
              <option value="">—</option>
              <option value="forward">Forward</option>
              <option value="defense">Defense</option>
              <option value="goalie">Goalie</option>
              <option value="flex">Flex</option>
            </select>
          </Field>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '14px 0' }}>
          <Toggle label="Starter" checked={isStarting} onChange={setIsStarting} />
          <Toggle label="Captain" checked={isCaptain}
            onChange={(v) => { setIsCaptain(v); if (v) setIsAlt(false); }} />
          <Toggle label="Alt-Cpt" checked={isAlt}
            onChange={(v) => { setIsAlt(v); if (v) setIsCaptain(false); }} />
          <Toggle label="Affiliate" checked={isAffiliate} onChange={setIsAffiliate} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn-ghost"
            style={{ color: 'var(--penalty)', borderColor: 'var(--penalty-a30)' }}
            onClick={remove} disabled={saving}>
            <Trash2 size={12} style={{ marginRight: 4 }} />
            Remove
          </button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="btn-ice" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function AddCoachModal({ leagueId, gameId, sheetId, onClose, onAdded }) {
  const [name, setName] = useState('');
  const [position, setPosition] = useState('head_coach');
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/games/${gameId}/sheets/${sheetId}/coaches`,
        { coach_name: name.trim(), coach_position: position });
      toast.success('Added');
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not add');
    } finally {
      setSaving(false);
    }
  }
  return (
    <ModalBackdrop onClose={onClose}>
      <div className="card" style={{ maxWidth: 440, width: '100%' }}>
        <ModalHeader title="Add bench staff" onClose={onClose} />
        <Field label="Name">
          <input type="text" value={name} maxLength={120}
            onChange={(e) => setName(e.target.value)} autoFocus
            placeholder="e.g. Sam Rivera" style={inputStyle} />
        </Field>
        <Field label="Position">
          <select value={position} onChange={(e) => setPosition(e.target.value)} style={inputStyle}>
            {COACH_POSITION_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving}>
            {saving ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function FinalizeModal({ leagueId, gameId, sheetId, teamName, onClose, onDone }) {
  const [name, setName] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const [saving, setSaving] = useState(false);
  const padRef = useRef(null);

  async function submit() {
    if (!name.trim()) return toast.error('Signing name is required');
    const signature = padRef.current?.getDataURL();
    if (!signature) return toast.error('Sign in the box above before finalizing');
    setSaving(true);
    try {
      const { data } = await api.post(
        `/leagues/${leagueId}/games/${gameId}/sheets/${sheetId}/finalize`,
        { signed_by_name: name.trim(), signature_b64: signature }
      );
      toast.success('Lineup finalized');
      // If THIS finalize was the last condition needed, the game is
      // now scoresheet-locked. Show a slightly different toast so
      // the user knows the game is complete.
      if (data?.scoresheet_lock?.locked && !data.scoresheet_lock.was_locked) {
        toast.success(
          `Scoresheet locked · final score ${data.scoresheet_lock.home}-${data.scoresheet_lock.away}`,
          { duration: 6000 }
        );
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not finalize');
    } finally {
      setSaving(false);
    }
  }
  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile pass — same vh/keyboard fix; this modal has the signing-name
          input above the signature pad, so keeping the Finalize button
          visible above the keyboard matters most here. */}
      <div className="card" style={{ maxWidth: 480, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title={`Finalize ${teamName}'s lineup`} onClose={onClose} />
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
          Once you finalize, the lineup is locked. You can reopen it before officials sign off.
        </p>
        <Field label="Your name (for the record)">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Coach Alex Foster" maxLength={120} autoFocus
            style={inputStyle} />
        </Field>
        <div style={{ marginBottom: 14 }}>
          <SignaturePad ref={padRef} label="Signature" onChange={setHasSignature} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice"
            onClick={submit}
            disabled={saving || !name.trim() || !hasSignature}>
            {saving ? 'Finalizing…' : 'Finalize'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ═══════════════════════════════════════════════════════════════
// SCORING TAB
// ═══════════════════════════════════════════════════════════════

function ScoringTab({ leagueId, gameId, game, goals, lineup, homeSheet, awaySheet, editableTeamIds, locked, onChanged }) {
  const [adding, setAdding] = useState(null);  // teamId being scored on

  const homeGoals = goals.filter(g => g.team_id === game.home_team_id);
  const awayGoals = goals.filter(g => g.team_id === game.away_team_id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TeamGoalsCard
        side="Home" teamId={game.home_team_id} teamName={game.home_team_name}
        goals={homeGoals}
        canEdit={!locked && editableTeamIds.includes(game.home_team_id)}
        leagueId={leagueId} gameId={gameId}
        onAddGoal={() => setAdding({ teamId: game.home_team_id, teamName: game.home_team_name })}
        onChanged={onChanged} />
      <TeamGoalsCard
        side="Away" teamId={game.away_team_id} teamName={game.away_team_name}
        goals={awayGoals}
        canEdit={!locked && editableTeamIds.includes(game.away_team_id)}
        leagueId={leagueId} gameId={gameId}
        onAddGoal={() => setAdding({ teamId: game.away_team_id, teamName: game.away_team_name })}
        onChanged={onChanged} />

      {adding && (
        <RecordGoalModal
          leagueId={leagueId} gameId={gameId}
          teamId={adding.teamId} teamName={adding.teamName}
          lineupRows={
            adding.teamId === game.home_team_id
              ? (homeSheet ? lineup[homeSheet.id] || [] : [])
              : (awaySheet ? lineup[awaySheet.id] || [] : [])
          }
          onClose={() => setAdding(null)}
          onSaved={() => { setAdding(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function TeamGoalsCard({ side, teamId, teamName, goals, canEdit, leagueId, gameId, onAddGoal, onChanged }) {
  async function removeGoal(g) {
    if (!window.confirm(`Remove goal by ${g.scorer_name} in ${PERIOD_LABELS[g.period]}?`)) return;
    try {
      await api.delete(`/leagues/${leagueId}/games/${gameId}/goals/${g.id}`);
      toast.success('Goal removed');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not remove');
    }
  }
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 14, borderBottom: '1px solid var(--border)',
        background: side === 'Home' ? 'rgba(0,196,255,0.04)' : 'rgba(255,90,90,0.04)',
      }}>
        <SectionHeader side={side} teamName={teamName} />
        {canEdit && (
          <button type="button" className="btn-ice" style={{ fontSize: 12, padding: '6px 14px' }} onClick={onAddGoal}>
            <Target size={12} style={{ marginRight: 6 }} />
            Goal
          </button>
        )}
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 11, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 8 }}>
          {goals.length} goal{goals.length === 1 ? '' : 's'}
        </div>
        {goals.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
            No goals yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {goals.map(g => (
              <GoalRow key={g.id} goal={g} canEdit={canEdit} onRemove={() => removeGoal(g)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GoalRow({ goal, canEdit, onRemove }) {
  const tags = [];
  if (goal.is_power_play)   tags.push('PP');
  if (goal.is_short_handed) tags.push('SH');
  if (goal.is_empty_net)    tags.push('EN');
  if (goal.is_shootout)     tags.push('SO');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px',
      background: 'var(--surface-2)', borderRadius: 8,
      border: '1px solid var(--border)',
    }}>
      <div style={{
        flexShrink: 0,
        width: 40, padding: '4px 6px',
        background: 'var(--surface)', borderRadius: 6,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-2)' }}>{PERIOD_LABELS[goal.period]}</div>
        <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
          {goal.time_in_period || '—'}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {goal.scorer_name}
          {goal.scorer_is_ad_hoc && <span style={{ fontSize: 9, color: 'var(--puck)', marginLeft: 6 }}>ad-hoc</span>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1 }}>
          {goal.assist1_name && (
            <>A: {goal.assist1_name}
              {goal.assist2_name && <>, {goal.assist2_name}</>}
            </>
          )}
          {!goal.assist1_name && <>Unassisted</>}
        </div>
      </div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {tags.map(t => <Badge key={t} label={t} color="var(--goal)" />)}
        </div>
      )}
      {canEdit && (
        <button type="button" className="btn-ghost"
          style={{ ...tinyBtn, color: 'var(--penalty)' }} onClick={onRemove}>
          <Trash2 size={10} />
        </button>
      )}
    </div>
  );
}

function RecordGoalModal({ leagueId, gameId, teamId, teamName, lineupRows, onClose, onSaved }) {
  const [scorer, setScorer]     = useState(null);  // {profile_id} or {ad_hoc_name}
  const [assist1, setAssist1]   = useState(null);
  const [assist2, setAssist2]   = useState(null);
  const [period, setPeriod]     = useState(1);
  const [time, setTime]         = useState('');
  const [pp, setPP]             = useState(false);
  const [sh, setSH]             = useState(false);
  const [en, setEN]             = useState(false);
  const [so, setSO]             = useState(false);
  const [saving, setSaving]     = useState(false);

  async function submit() {
    if (!scorer) return toast.error('Pick a scorer');
    if (time && !/^\d{1,2}:\d{2}$/.test(time)) return toast.error('Time must be MM:SS');

    const body = {
      team_id: teamId,
      scorer,
      assist1: assist1 || null,
      assist2: assist2 || null,
      period,
      time_in_period: time || null,
      is_power_play: pp,
      is_short_handed: sh,
      is_empty_net: en,
      is_shootout: so,
    };
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/games/${gameId}/goals`, body);
      toast.success('Goal recorded');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not record goal');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile pass — same vh/keyboard fix as AddPlayerModal above. */}
      <div className="card" style={{ maxWidth: 560, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title={`Goal · ${teamName}`} onClose={onClose} />

        <PlayerPickField label="Scorer (required)" required value={scorer} setValue={setScorer} lineupRows={lineupRows} />
        <PlayerPickField label="Assist 1 (optional)" value={assist1} setValue={setAssist1} lineupRows={lineupRows} />
        <PlayerPickField label="Assist 2 (optional)" value={assist2} setValue={setAssist2} lineupRows={lineupRows} />

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Field label="Period" style={{ flex: '1 1 120px' }}>
            <select value={period} onChange={(e) => setPeriod(parseInt(e.target.value, 10))} style={inputStyle}>
              <option value={1}>1st</option>
              <option value={2}>2nd</option>
              <option value={3}>3rd</option>
              <option value={4}>OT</option>
              <option value={5}>SO</option>
            </select>
          </Field>
          <Field label="Time (MM:SS)" style={{ flex: '1 1 120px' }}>
            <input type="text" value={time} onChange={(e) => setTime(e.target.value)}
              placeholder="12:34" maxLength={5}
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </Field>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '14px 0' }}>
          <Toggle label="PP" checked={pp} onChange={setPP} />
          <Toggle label="SH" checked={sh} onChange={setSH} />
          <Toggle label="EN" checked={en} onChange={setEN} />
          <Toggle label="SO" checked={so} onChange={setSO} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving || !scorer}>
            {saving ? 'Saving…' : 'Record'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ═══════════════════════════════════════════════════════════════
// PENALTIES TAB
// ═══════════════════════════════════════════════════════════════

function PenaltiesTab({ leagueId, gameId, game, penalties, lineup, homeSheet, awaySheet, editableTeamIds, locked, onChanged }) {
  const [adding, setAdding] = useState(null);

  const homePenalties = penalties.filter(p => p.team_id === game.home_team_id);
  const awayPenalties = penalties.filter(p => p.team_id === game.away_team_id);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TeamPenaltiesCard
        side="Home" teamId={game.home_team_id} teamName={game.home_team_name}
        penalties={homePenalties}
        canEdit={!locked && editableTeamIds.includes(game.home_team_id)}
        leagueId={leagueId} gameId={gameId}
        onAdd={() => setAdding({ teamId: game.home_team_id, teamName: game.home_team_name })}
        onChanged={onChanged} />
      <TeamPenaltiesCard
        side="Away" teamId={game.away_team_id} teamName={game.away_team_name}
        penalties={awayPenalties}
        canEdit={!locked && editableTeamIds.includes(game.away_team_id)}
        leagueId={leagueId} gameId={gameId}
        onAdd={() => setAdding({ teamId: game.away_team_id, teamName: game.away_team_name })}
        onChanged={onChanged} />

      {adding && (
        <RecordPenaltyModal
          leagueId={leagueId} gameId={gameId}
          teamId={adding.teamId} teamName={adding.teamName}
          lineupRows={
            adding.teamId === game.home_team_id
              ? (homeSheet ? lineup[homeSheet.id] || [] : [])
              : (awaySheet ? lineup[awaySheet.id] || [] : [])
          }
          onClose={() => setAdding(null)}
          onSaved={() => { setAdding(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function TeamPenaltiesCard({ side, teamId, teamName, penalties, canEdit, leagueId, gameId, onAdd, onChanged }) {
  async function removePen(p) {
    if (!window.confirm(`Remove ${p.infraction} penalty on ${p.player_name}?`)) return;
    try {
      await api.delete(`/leagues/${leagueId}/games/${gameId}/penalties/${p.id}`);
      toast.success('Penalty removed');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not remove');
    }
  }
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 14, borderBottom: '1px solid var(--border)',
      }}>
        <SectionHeader side={side} teamName={teamName} />
        {canEdit && (
          <button type="button" className="btn-ice" style={{ fontSize: 12, padding: '6px 14px' }} onClick={onAdd}>
            <AlertOctagon size={12} style={{ marginRight: 6 }} />
            Penalty
          </button>
        )}
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 11, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 8 }}>
          {penalties.length} penalt{penalties.length === 1 ? 'y' : 'ies'}
          {' · '}
          {penalties.reduce((a, p) => a + p.minutes, 0)} PIM
        </div>
        {penalties.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
            No penalties yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {penalties.map(p => (
              <PenaltyRow key={p.id} pen={p} canEdit={canEdit} onRemove={() => removePen(p)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PenaltyRow({ pen, canEdit, onRemove }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 10px',
      background: 'var(--surface-2)', borderRadius: 8,
      border: '1px solid var(--border)',
    }}>
      <div style={{
        flexShrink: 0,
        width: 40, padding: '4px 6px',
        background: 'var(--surface)', borderRadius: 6,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-2)' }}>{PERIOD_LABELS[pen.period]}</div>
        <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
          {pen.time_in_period || '—'}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {pen.player_name}
          {pen.player_is_ad_hoc && <span style={{ fontSize: 9, color: 'var(--puck)', marginLeft: 6 }}>ad-hoc</span>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1 }}>
          {pen.infraction} · {pen.minutes} min
          {pen.is_major && <> · major</>}
          {pen.is_misconduct && <> · misconduct</>}
        </div>
      </div>
      {canEdit && (
        <button type="button" className="btn-ghost"
          style={{ ...tinyBtn, color: 'var(--penalty)' }} onClick={onRemove}>
          <Trash2 size={10} />
        </button>
      )}
    </div>
  );
}

function RecordPenaltyModal({ leagueId, gameId, teamId, teamName, lineupRows, onClose, onSaved }) {
  const [player, setPlayer]   = useState(null);
  const [infraction, setInfraction] = useState('');
  const [minutes, setMinutes] = useState(2);
  const [period, setPeriod]   = useState(1);
  const [time, setTime]       = useState('');
  const [isMajor, setIsMajor] = useState(false);
  const [isMisconduct, setIsMisconduct] = useState(false);
  const [saving, setSaving]   = useState(false);

  async function submit() {
    if (!player) return toast.error('Pick a player');
    if (!infraction.trim()) return toast.error('Infraction is required');
    const min = parseInt(minutes, 10);
    if (!Number.isInteger(min) || min <= 0 || min > 60) return toast.error('Minutes must be 1-60');
    if (time && !/^\d{1,2}:\d{2}$/.test(time)) return toast.error('Time must be MM:SS');

    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/games/${gameId}/penalties`, {
        team_id: teamId,
        player,
        infraction: infraction.trim(),
        minutes: min,
        period,
        time_in_period: time || null,
        is_major: isMajor,
        is_misconduct: isMisconduct,
      });
      toast.success('Penalty recorded');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not record');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile pass — same vh/keyboard fix as AddPlayerModal above. */}
      <div className="card" style={{ maxWidth: 560, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title={`Penalty · ${teamName}`} onClose={onClose} />

        <PlayerPickField label="Player (required)" required value={player} setValue={setPlayer} lineupRows={lineupRows} />

        <Field label="Infraction">
          <input type="text" value={infraction} onChange={(e) => setInfraction(e.target.value)}
            placeholder="e.g. Tripping" maxLength={60}
            list="infractions-list"
            style={inputStyle} />
          <datalist id="infractions-list">
            {COMMON_INFRACTIONS.map(i => <option key={i} value={i} />)}
          </datalist>
        </Field>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Field label="Minutes" style={{ flex: '1 1 100px' }}>
            <select value={minutes} onChange={(e) => setMinutes(parseInt(e.target.value, 10))} style={inputStyle}>
              <option value={2}>2 (minor)</option>
              <option value={4}>4 (double minor)</option>
              <option value={5}>5 (major)</option>
              <option value={10}>10 (misconduct)</option>
            </select>
          </Field>
          <Field label="Period" style={{ flex: '1 1 100px' }}>
            <select value={period} onChange={(e) => setPeriod(parseInt(e.target.value, 10))} style={inputStyle}>
              <option value={1}>1st</option>
              <option value={2}>2nd</option>
              <option value={3}>3rd</option>
              <option value={4}>OT</option>
              <option value={5}>SO</option>
            </select>
          </Field>
          <Field label="Time (MM:SS)" style={{ flex: '1 1 120px' }}>
            <input type="text" value={time} onChange={(e) => setTime(e.target.value)}
              placeholder="08:42" maxLength={5}
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </Field>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '14px 0' }}>
          <Toggle label="Major" checked={isMajor} onChange={setIsMajor} />
          <Toggle label="Misconduct" checked={isMisconduct} onChange={setIsMisconduct} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving || !player}>
            {saving ? 'Saving…' : 'Record'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

// ═══════════════════════════════════════════════════════════════
// SIGN-OFF TAB — Phase 5b
// ═══════════════════════════════════════════════════════════════
//
// Two pieces:
//   1. Status overview — coach sheets (with names + signatures),
//      officials list (with sign state), and the game-lock state.
//   2. "Sign as <role>" panel — only shown for the current user's
//      OWN officials assignments that haven't been signed yet.
//      Uses the existing Phase 1 endpoint
//      POST /games/:gameId/officials/:officialId/sign.

const OFFICIAL_ROLE_LABELS = {
  head_ref:    'Head referee',
  linesman:    'Linesman',
  timekeeper:  'Timekeeper',
  scorekeeper: 'Scorekeeper',
};

function SignoffTab({ leagueId, gameId, game, homeSheet, awaySheet, locked, onChanged }) {
  const [officials, setOfficials] = useState([]);
  const [meId, setMeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overriding, setOverriding] = useState(false);

  // Manager override visibility uses the league membership hook.
  // Only managers / users with manage_schedule see the force-lock
  // and force-unlock controls.
  const { isManager, can } = useLeagueMembership(leagueId);
  const canOverride = isManager || can('manage_schedule');

  async function load() {
    setLoading(true);
    try {
      const [oRes, meRes] = await Promise.all([
        api.get(`/leagues/${leagueId}/games/${gameId}/officials`),
        api.get('/auth/me').catch(() => ({ data: null })),
      ]);
      setOfficials(oRes.data?.officials || []);
      setMeId(meRes.data?.id || null);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load officials');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gameId]);

  async function forceLock() {
    if (!window.confirm(
      'Force-lock the scoresheet?\n\n' +
      'This finalizes the game using the current goal data as the score, ' +
      'even though not all sign-off conditions are met. Use sparingly — ' +
      'this is logged to the system event log.'
    )) return;
    setOverriding(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/games/${gameId}/sheets/force-lock`);
      toast.success(`Scoresheet locked · final score ${data.home_score}-${data.away_score}`,
        { duration: 6000 });
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not force-lock');
    } finally {
      setOverriding(false);
    }
  }

  async function forceUnlock() {
    if (!window.confirm(
      'Unlock the scoresheet?\n\n' +
      'This reopens the game for edits. Officials may need to re-sign ' +
      'after corrections. Logged to the system event log.'
    )) return;
    setOverriding(true);
    try {
      await api.post(`/leagues/${leagueId}/games/${gameId}/sheets/force-unlock`);
      toast.success('Scoresheet unlocked');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not unlock');
    } finally {
      setOverriding(false);
    }
  }

  if (loading) {
    return <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>;
  }

  const homeFinalized = homeSheet?.is_finalized;
  const awayFinalized = awaySheet?.is_finalized;
  const allOfficialsSigned = officials.length > 0 && officials.every(o => o.signed_at);
  const sheetsReady = !!(homeFinalized && awayFinalized);
  const mySignableAssignments = meId
    ? officials.filter(o => o.assigned_user_id === meId && !o.signed_at)
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Lock banner */}
      {locked ? (
        <div className="card" style={{
          background: 'var(--goal-a20)', borderColor: 'var(--goal-a40)',
          padding: 14, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Lock size={18} color="var(--goal)" />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Scoresheet final</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
              Both lineups finalized and all officials signed.
              Final score{' '}
              <strong>{game.result_home_score ?? '?'}-{game.result_away_score ?? '?'}</strong>.
            </div>
          </div>
        </div>
      ) : (
        <div className="card" style={{
          background: 'var(--surface-2)', padding: 14,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Sign-off progress
          </div>
          <ProgressItem
            label={`${game.home_team_name || 'Home'} lineup finalized`}
            done={homeFinalized} />
          <ProgressItem
            label={`${game.away_team_name || 'Away'} lineup finalized`}
            done={awayFinalized} />
          <ProgressItem
            label={`All officials signed (${officials.filter(o => o.signed_at).length}/${officials.length || '0'})`}
            done={allOfficialsSigned}
            disabled={officials.length === 0} />
          {officials.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6, fontStyle: 'italic' }}>
              Assign at least one official to enable scoresheet locking.
            </div>
          )}
        </div>
      )}

      {/* My signable assignments (if any) */}
      {!locked && mySignableAssignments.length > 0 && (
        <>
          {!sheetsReady && (
            <div className="card" style={{
              background: 'var(--puck-a15)', borderColor: 'var(--puck-a40)',
              padding: 12, fontSize: 12,
            }}>
              Both teams need to finalize their lineups before officials can sign.
              You'll be able to sign here once that's done.
            </div>
          )}
          {sheetsReady && mySignableAssignments.map(o => (
            <MyOfficialSignCard key={o.id}
              official={o}
              leagueId={leagueId} gameId={gameId}
              onSigned={() => { load(); onChanged(); }} />
          ))}
        </>
      )}

      {/* Manager override — force-lock or force-unlock. Only managers
          see this. Always at the end so it doesn't compete for
          attention with the normal sign-off flow above. */}
      {canOverride && (
        <div className="card" style={{
          padding: 14,
          borderColor: 'var(--puck-a40)',
          borderStyle: 'dashed',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <AlertTriangle size={14} color="var(--puck)" />
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Manager override
            </span>
          </div>
          {locked ? (
            <>
              <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>
                Need to correct the scoresheet? Unlock to reopen it for edits.
                Coach sign-offs and official signatures are preserved; you may need them to re-sign after corrections.
              </p>
              <button type="button" className="btn-ghost" onClick={forceUnlock}
                disabled={overriding}
                style={{ color: 'var(--puck)', borderColor: 'var(--puck-a40)' }}>
                <Unlock size={12} style={{ marginRight: 6 }} />
                {overriding ? 'Unlocking…' : 'Unlock scoresheet'}
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 10 }}>
                Force-lock the scoresheet without waiting for all sign-offs. Use this when a game was
                played without officials, or for any case where the normal lock conditions can't be met.
                Logged for audit.
              </p>
              <button type="button" className="btn-ghost" onClick={forceLock}
                disabled={overriding}
                style={{ color: 'var(--puck)', borderColor: 'var(--puck-a40)' }}>
                <Lock size={12} style={{ marginRight: 6 }} />
                {overriding ? 'Locking…' : 'Force-lock scoresheet'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Coach sheets — display-only */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: 12, borderBottom: '1px solid var(--border)',
          fontSize: 12, fontWeight: 700, color: 'var(--text-2)',
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          Coach signatures
        </div>
        <CoachSignRow side="Home" teamName={game.home_team_name} sheet={homeSheet} />
        <CoachSignRow side="Away" teamName={game.away_team_name} sheet={awaySheet} isLast />
      </div>

      {/* Officials list — display-only (assignments + sign state) */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: 12, borderBottom: '1px solid var(--border)',
          fontSize: 12, fontWeight: 700, color: 'var(--text-2)',
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          Officials
        </div>
        {officials.length === 0 ? (
          <div style={{ padding: 14, fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>
            No officials assigned. Use the{' '}
            <Link to={`/league/${leagueId}/officials?game=${gameId}`} style={{ color: 'var(--ice)' }}>
              Officials page
            </Link>{' '}
            to assign refs and scorekeepers.
          </div>
        ) : (
          officials.map((o, i) => (
            <OfficialSignRow key={o.id}
              official={o}
              isLast={i === officials.length - 1}
              isMine={meId && o.assigned_user_id === meId}
              canManage={canOverride}
              sheetsReady={sheetsReady}
              locked={locked}
              leagueId={leagueId}
              gameId={gameId}
              onSigned={() => { load(); onChanged(); }} />
          ))
        )}
      </div>
    </div>
  );
}

function ProgressItem({ label, done, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        background: disabled ? 'transparent' : (done ? 'var(--goal)' : 'var(--surface)'),
        border: `1.5px solid ${disabled ? 'var(--text-3)' : (done ? 'var(--goal)' : 'var(--border)')}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {done && !disabled && <Check size={11} color="var(--btn-accent-text)" strokeWidth={3} />}
      </div>
      <div style={{
        fontSize: 13,
        color: disabled ? 'var(--text-3)' : (done ? 'var(--text)' : 'var(--text-2)'),
        textDecoration: disabled ? 'line-through' : 'none',
      }}>
        {label}
      </div>
    </div>
  );
}

function CoachSignRow({ side, teamName, sheet, isLast }) {
  const sig = sheet?.signature_b64;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: 12,
      borderBottom: isLast ? 'none' : '1px solid var(--border)',
    }}>
      <span style={{
        padding: '2px 8px', borderRadius: 6, flexShrink: 0,
        background: side === 'Home' ? 'var(--ice-a10)' : 'var(--puck-a15)',
        color:      side === 'Home' ? 'var(--ice)'    : 'var(--puck)',
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7,
      }}>{side}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{teamName}</div>
        {sheet?.is_finalized ? (
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
            Signed by <strong style={{ color: 'var(--text)' }}>{sheet.signed_by_name}</strong>{' '}
            on {formatDateTime(sheet.signed_at)}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, fontStyle: 'italic' }}>
            Lineup not yet finalized
          </div>
        )}
      </div>
      {/* Signature thumbnail. Note: GET /sheets returns the full
          signature_b64 to authorized readers. If the column is null
          (Phase 5a finalize without sig), we just show a placeholder. */}
      {sig
        ? <img src={sig} alt="signature" style={{
            height: 36, width: 92,
            objectFit: 'contain', objectPosition: 'left center',
            background: '#fff', borderRadius: 4, padding: 2,
            flexShrink: 0,
          }} />
        : <div style={{
            height: 36, width: 92, flexShrink: 0,
            background: 'var(--surface-2)', borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, color: 'var(--text-3)',
          }}>
            {sheet?.is_finalized ? 'no signature' : '—'}
          </div>}
    </div>
  );
}

function OfficialSignRow({ official, isLast, isMine, canManage, sheetsReady, locked, leagueId, gameId, onSigned }) {
  const signed = !!official.signed_at;
  const [showAdhoc, setShowAdhoc] = useState(false);
  // v2.5.157 — ad-hoc sign-off. An official assigned WITHOUT a user
  // account (assigned_user_id === null) can't sign themselves — there's
  // no login for them. Previously the backend POST .../sign-as-adhoc
  // existed but nothing in the UI called it, so a league using ad-hoc
  // refs/scorekeepers could never complete sign-off and the scoresheet
  // could never lock the normal way (only via force-lock). A manager
  // can now record the ad-hoc official's signature on their behalf.
  // Shown only when: manager, the official has no user account, isn't
  // already signed, both lineups are finalized, and the sheet isn't
  // already locked.
  const isAdhoc = official.assigned_user_id == null;
  const canSignAdhoc = canManage && isAdhoc && !signed && sheetsReady && !locked;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: 12,
      borderBottom: isLast ? 'none' : '1px solid var(--border)',
      background: isMine ? 'var(--ice-a10)' : 'transparent',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: signed ? 'var(--goal-a20)' : 'var(--surface-2)',
        border: `1px solid ${signed ? 'var(--goal)' : 'var(--border)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {signed
          ? <Check size={13} color="var(--goal)" />
          : <Shield size={12} color="var(--text-2)" />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {OFFICIAL_ROLE_LABELS[official.role] || official.role}
          {isMine && (
            <span style={{
              marginLeft: 8, fontSize: 9, fontWeight: 800,
              padding: '1px 5px', borderRadius: 4,
              background: 'var(--ice-a10)', color: 'var(--ice)',
              letterSpacing: 0.5,
            }}>YOU</span>
          )}
          {isAdhoc && !signed && (
            <span style={{
              marginLeft: 8, fontSize: 9, fontWeight: 800,
              padding: '1px 5px', borderRadius: 4,
              background: 'var(--surface-2)', color: 'var(--text-3)',
              letterSpacing: 0.5,
            }}>NO ACCOUNT</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
          {signed
            ? <>Signed by <strong style={{ color: 'var(--text)' }}>{official.official_name}</strong>{' '}
                on {formatDateTime(official.signed_at)}</>
            : (official.assigned_user_name || official.official_name || '(awaiting sign-off)')}
        </div>
      </div>
      {canSignAdhoc && (
        <button type="button" className="btn-ghost"
          onClick={() => setShowAdhoc(true)}
          style={{ flexShrink: 0, fontSize: 11, padding: '6px 10px' }}
          title="Record this official's signature on their behalf">
          <PenLine size={11} style={{ marginRight: 5 }} />
          Sign
        </button>
      )}
      {showAdhoc && (
        <AdhocSignModal
          official={official}
          leagueId={leagueId} gameId={gameId}
          onClose={() => setShowAdhoc(false)}
          onSigned={() => { setShowAdhoc(false); onSigned && onSigned(); }} />
      )}
    </div>
  );
}

// v2.5.157 — modal for a manager to record an ad-hoc official's
// signature (officials with no user account). Mirrors MyOfficialSignCard
// but targets the sign-as-adhoc endpoint and is presented as a modal
// from the officials list rather than an inline self-sign card.
function AdhocSignModal({ official, leagueId, gameId, onClose, onSigned }) {
  const [name, setName] = useState(official.official_name || official.assigned_user_name || '');
  const [hasSignature, setHasSignature] = useState(false);
  const [saving, setSaving] = useState(false);
  const padRef = useRef(null);

  async function submit() {
    if (!name.trim()) return toast.error("The official's name is required for the record");
    const signature = padRef.current?.getDataURL();
    if (!signature) return toast.error('Sign in the box above');
    setSaving(true);
    try {
      const { data } = await api.post(
        `/leagues/${leagueId}/games/${gameId}/officials/${official.id}/sign-as-adhoc`,
        { official_name: name.trim(), signature_b64: signature }
      );
      toast.success(`Recorded signature for ${OFFICIAL_ROLE_LABELS[official.role] || official.role}`);
      if (data?.scoresheet_lock?.locked && !data.scoresheet_lock.was_locked) {
        toast.success(
          `Scoresheet locked · final score ${data.scoresheet_lock.home}-${data.scoresheet_lock.away}`,
          { duration: 6000 }
        );
      }
      onSigned();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not record signature');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      {/* mobile pass — same vh/keyboard fix; name input sits above the
          signature pad here too. */}
      <div className="card" style={{ maxWidth: 480, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.92)', overflowY: 'auto' }}>
        <ModalHeader title={`Sign off — ${OFFICIAL_ROLE_LABELS[official.role] || official.role}`} onClose={onClose} />
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 14 }}>
          This official doesn't have an app account, so you can record their signature on their behalf.
          They should sign in the box below.
        </p>
        <Field label="Official's name">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Full name" maxLength={120} autoFocus
            style={inputStyle} />
        </Field>
        <div style={{ marginBottom: 14 }}>
          <SignaturePad ref={padRef} label="Signature" onChange={setHasSignature} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-ice" onClick={submit} disabled={saving || !hasSignature}>
            {saving ? 'Saving…' : 'Record signature'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function MyOfficialSignCard({ official, leagueId, gameId, onSigned }) {
  const [name, setName] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const [saving, setSaving] = useState(false);
  const padRef = useRef(null);

  async function submit() {
    if (!name.trim()) return toast.error('Your name is required for the record');
    const signature = padRef.current?.getDataURL();
    if (!signature) return toast.error('Sign in the box above');
    setSaving(true);
    try {
      const { data } = await api.post(
        `/leagues/${leagueId}/games/${gameId}/officials/${official.id}/sign`,
        { official_name: name.trim(), signature_b64: signature }
      );
      toast.success(`Signed off as ${OFFICIAL_ROLE_LABELS[official.role] || official.role}`);
      if (data?.scoresheet_lock?.locked && !data.scoresheet_lock.was_locked) {
        toast.success(
          `Scoresheet locked · final score ${data.scoresheet_lock.home}-${data.scoresheet_lock.away}`,
          { duration: 6000 }
        );
      }
      onSigned();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not sign off');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{
      borderColor: 'var(--ice-a40)',
      borderWidth: 2,
      padding: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Award size={14} color="var(--ice)" />
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          Sign as {OFFICIAL_ROLE_LABELS[official.role] || official.role}
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 12 }}>
        Your sign-off certifies the lineup and stats as recorded. When all officials have signed,
        the scoresheet locks and the score becomes final.
      </p>
      <Field label="Your name (for the record)">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Alex Foster" maxLength={120}
          style={inputStyle} />
      </Field>
      <div style={{ marginBottom: 12 }}>
        <SignaturePad ref={padRef} label="Signature" onChange={setHasSignature} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn-ice" onClick={submit}
          disabled={saving || !name.trim() || !hasSignature}>
          {saving ? 'Signing…' : `Sign as ${OFFICIAL_ROLE_LABELS[official.role] || official.role}`}
        </button>
      </div>
    </div>
  );
}

function PlayerPickField({ label, required, value, setValue, lineupRows }) {
  const [mode, setMode] = useState('roster');  // 'roster' | 'adhoc'
  const [search, setSearch] = useState('');
  const [adHocName, setAdHocName] = useState(value?.ad_hoc_name || '');

  // Sync adHocName upward as the user types — debounce-less since
  // it's a tiny string and the parent only reads on submit anyway.
  function setAdHoc(name) {
    setAdHocName(name);
    setValue(name.trim() ? { ad_hoc_name: name.trim() } : null);
  }
  function pickRoster(row) {
    if (!row.player_profile_id) {
      // Ad-hoc rows in the lineup don't have profile_id — fall back
      // to recording the goal/penalty as ad-hoc with their name.
      setValue({ ad_hoc_name: row.ad_hoc_name || row.display_name || '(unknown)' });
    } else {
      setValue({ profile_id: row.player_profile_id });
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lineupRows;
    return lineupRows.filter(r => {
      const n = (r.display_name || r.ad_hoc_name || '').toLowerCase();
      const j = String(r.jersey_number || '');
      return n.includes(q) || j === q;
    });
  }, [lineupRows, search]);

  const selectedProfileId = value?.profile_id || null;
  const selectedAdHoc = value?.ad_hoc_name || null;

  return (
    <Field label={label}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        <SmallToggle active={mode === 'roster'} onClick={() => setMode('roster')}>Roster</SmallToggle>
        <SmallToggle active={mode === 'adhoc'}  onClick={() => setMode('adhoc')}>Ad-hoc</SmallToggle>
      </div>
      {mode === 'roster' && (
        <>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or jersey #…"
            style={{ ...inputStyle, marginBottom: 4 }} />
          <div style={{
            maxHeight: 160, overflowY: 'auto',
            border: '1px solid var(--border)', borderRadius: 8,
            background: 'var(--surface-2)',
          }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>
                {lineupRows.length === 0
                  ? 'No lineup added yet. Use ad-hoc mode for guests.'
                  : 'No matches.'}
              </div>
            ) : (
              filtered.map(r => {
                const sel = selectedProfileId && r.player_profile_id === selectedProfileId;
                return (
                  <button key={r.id} type="button" onClick={() => pickRoster(r)}
                    style={{
                      width: '100%', textAlign: 'left',
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 10px',
                      background: sel ? 'var(--ice-a10)' : 'transparent',
                      border: 'none', borderBottom: '1px solid var(--border)',
                      cursor: 'pointer', color: 'var(--text)',
                    }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: 6,
                      background: 'var(--ice-a10)', color: 'var(--ice)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800, fontFamily: 'var(--font-mono)',
                      flexShrink: 0,
                    }}>{r.jersey_number}</span>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.display_name || r.ad_hoc_name}
                    </span>
                    {sel && <Check size={12} color="var(--ice)" />}
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
      {mode === 'adhoc' && (
        <input type="text" value={adHocName} onChange={(e) => setAdHoc(e.target.value)}
          placeholder="Name (e.g. guest sub)" maxLength={120}
          style={inputStyle} />
      )}
      {required && !value && (
        <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>Required.</div>
      )}
      {value && (
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4 }}>
          Selected: {value.ad_hoc_name || lineupRows.find(r => r.player_profile_id === value.profile_id)?.display_name || '?'}
        </div>
      )}
    </Field>
  );
}

function SmallToggle({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: 1, padding: '5px 8px', fontSize: 11, fontWeight: 600,
      background: active ? 'var(--ice-a10)' : 'var(--surface-2)',
      color: active ? 'var(--text)' : 'var(--text-2)',
      border: `1px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
      borderRadius: 6, cursor: 'pointer',
    }}>{children}</button>
  );
}

// ─── Shared bits ──────────────────────────────────────────────

function Toggle({ label, checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 999,
      border: `1px solid ${checked ? 'var(--ice-a40)' : 'var(--border)'}`,
      background: checked ? 'var(--ice-a10)' : 'transparent',
      color: checked ? 'var(--text)' : 'var(--text-2)',
      fontSize: 12, fontWeight: 600, cursor: 'pointer',
    }}>
      <div style={{
        width: 12, height: 12, borderRadius: 3,
        background: checked ? 'var(--ice)' : 'transparent',
        border: `1px solid ${checked ? 'var(--ice)' : 'var(--text-2)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <Check size={9} color="var(--btn-accent-text)" strokeWidth={3} />}
      </div>
      {label}
    </button>
  );
}
function ModeBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: '1 1 100px', padding: '8px 12px', borderRadius: 8,
      background: active ? 'var(--ice-a10)' : 'var(--surface-2)',
      border: `1px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
      color: active ? 'var(--text)' : 'var(--text-2)',
      fontWeight: 600, fontSize: 12, cursor: 'pointer',
    }}>{children}</button>
  );
}
function ModalBackdrop({ onClose, children }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, zIndex: 100,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}
function ModalHeader({ title, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
      <h2 className="section-title text-lg">{title}</h2>
      <button type="button" onClick={onClose}
        style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer' }}>
        <X size={18} />
      </button>
    </div>
  );
}
function Field({ label, hint, children, style }) {
  return (
    <label style={{ display: 'block', marginBottom: 12, ...style }}>
      <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 }}>
        {label}
      </span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{hint}</span>}
    </label>
  );
}
function formatDateTime(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString(); }
  catch { return s; }
}

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  outline: 'none',
};
const tinyBtn = { padding: '4px 8px', fontSize: 11, minHeight: 'auto' };
